import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import type { LayoutChangeEvent } from 'react-native';
import { GTFonts, theme } from '../../theme';
import { fs, s, v } from '../../utils/libs';

export type SlidingSegmentOption = {
  key: string;
  label: string;
};

export type SlidingSegmentedControlColors = {
  track: string;
  thumb: string;
  activeLabel: string;
  inactiveLabel: string;
  inactiveSegmentBg?: string;
};

type SlidingSegmentedControlProps = {
  options: SlidingSegmentOption[];
  activeIndex: number;
  onChange: (index: number) => void;
  colors: SlidingSegmentedControlColors;
  disabled?: boolean;
};

const TRACK_PADDING = s(3);

/**
 * Pill segmented control with a sliding thumb (same motion as Quick Actions).
 */
const SlidingSegmentedControl = ({
  options,
  activeIndex,
  onChange,
  colors,
  disabled,
}: SlidingSegmentedControlProps) => {
  const [trackWidth, setTrackWidth] = useState(0);
  const slotWidth =
    options.length > 0
      ? Math.max((trackWidth - TRACK_PADDING * 2) / options.length, 0)
      : 0;
  const offset = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(offset, {
      toValue: activeIndex * slotWidth,
      duration: 220,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [activeIndex, offset, slotWidth]);

  const handleLayout = (event: LayoutChangeEvent) =>
    setTrackWidth(event.nativeEvent.layout.width);

  return (
    <View
      onLayout={handleLayout}
      style={[styles.track, { backgroundColor: colors.track }]}>
      {slotWidth > 0 ? (
        <Animated.View
          pointerEvents="none"
          style={[
            styles.thumb,
            {
              width: slotWidth,
              backgroundColor: colors.thumb,
              transform: [{ translateX: offset }],
            },
          ]}
        />
      ) : null}
      {options.map((option, index) => (
        <TouchableOpacity
          key={option.key}
          disabled={disabled}
          onPress={() => onChange(index)}
          style={[
            styles.segment,
            index !== activeIndex &&
              colors.inactiveSegmentBg != null && {
                backgroundColor: colors.inactiveSegmentBg,
                borderRadius: theme.borderRadius.round,
              },
          ]}
          accessibilityRole="button"
          accessibilityState={{ selected: index === activeIndex }}>
          <Text
            numberOfLines={1}
            style={[
              styles.segmentLabel,
              {
                color:
                  index === activeIndex
                    ? colors.activeLabel
                    : colors.inactiveLabel,
              },
            ]}>
            {option.label}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );
};

export default SlidingSegmentedControl;

const styles = StyleSheet.create({
  track: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: theme.borderRadius.round,
    padding: TRACK_PADDING,
  },
  thumb: {
    position: 'absolute',
    top: TRACK_PADDING,
    bottom: TRACK_PADDING,
    left: TRACK_PADDING,
    borderRadius: theme.borderRadius.round,
  },
  segment: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: v(7),
    paddingHorizontal: s(4),
  },
  segmentLabel: {
    fontSize: fs(12),
    fontFamily: GTFonts.semibold,
    lineHeight: fs(14),
  },
});
